import CatalogCategories from "@/pages/configuration/delay-categories";
import CatalogAdjustmentReason from "@/pages/configuration/pages/adjustment-reasons";
import CatalogAdjustmentType from "@/pages/configuration/pages/adjustment-types";
import HeatAgeThresholds from "@/pages/configuration/pages/heat-age-thresholds";
import InspectionTemplates from "@/pages/configuration/pages/inspection-templates";
import SurfaceDefects from "@/pages/configuration/pages/surface-defects";
import ShiftReportConfig from "@/pages/configuration/pages/shift-report-config";
import TensileRequirements from "@/pages/configuration/pages/tensile-requirements";
import ChemistryRanges from "@/pages/configuration/pages/chemistry-ranges";
import TestsBySection from "@/pages/configuration/pages/tests-by-section";
import WaterMeterThresholds from "@/pages/configuration/pages/water-meter-thresholds";
import GasAllocations from "@/pages/configuration/gas-allocations";
import WaterAllocations from "@/pages/configuration/water-allocations";
import { type ConfigSidebarSection } from "@dexteel/mesf-core";
import {
	ArticleOutlined,
	AssignmentTurnedInOutlined,
	BoltOutlined,
	CommentOutlined,
	FactCheckOutlined,
	FitnessCenterOutlined,
	HandymanOutlined,
	HourglassBottomOutlined,
	LabelOutlined,
	LayersOutlined,
	LocalFireDepartmentOutlined,
	PauseCircleOutlined,
	PlaceOutlined,
	PrecisionManufacturingOutlined,
	ReportProblemOutlined,
	Science,
	ScienceOutlined,
	SpeedOutlined,
	StraightenOutlined,
	StyleOutlined,
	TagOutlined,
	TuneOutlined,
	WaterDropOutlined,
	WorkspacesOutlined,
} from "@mui/icons-material";
import { ReactElement, ReactNode } from "react";
import CatalogDelayCodes from "../pages/configuration/delay-codes";
import CatalogLocation from "../pages/configuration/pages/locations";
import SectionDimensions from "../pages/configuration/pages/section-dimensions";
import CatalogSectionFamily from "../pages/configuration/pages/section-families";
import Section from "../pages/configuration/pages/sections";
import { useAssetId } from "./hooks/useAssetId";
import {
	ConfigurationPath,
	isConfigurationAllowed,
} from "./mill-config/mill-extended-config";

export const configurationPath = "/configuration";

type SidebarItem = {
	path: ConfigurationPath;
	label: string;
	icon: ReactElement;
};

type SidebarSection = {
	name: string;
	icon: ReactElement;
	items: SidebarItem[];
};

type Component = {
	path: ConfigurationPath | `${ConfigurationPath}/${string}`;
	main: () => ReactNode;
};

const sidebarSections: SidebarSection[] = [
	{
		name: "Mill",
		icon: <PrecisionManufacturingOutlined />,
		items: [
			{
				path: "section-families",
				label: "Section Families",
				icon: <WorkspacesOutlined />,
			},
			{ path: "sections", label: "Sections", icon: <LayersOutlined /> },
			{
				path: "section-dimensions",
				label: "Section Dimensions",
				icon: <StraightenOutlined />,
			},
			{ path: "locations", label: "Locations", icon: <PlaceOutlined /> },
		],
	},
	{
		name: "Threshold",
		icon: <TuneOutlined />,
		items: [
			{
				path: "shift-report-config",
				label: "Threshold Adjustments",
				icon: <TuneOutlined />,
			},
		],
	},
	{
		name: "Delays",
		icon: <PauseCircleOutlined />,
		items: [
			{
				path: "delay-categories",
				label: "Categories",
				icon: <LabelOutlined />,
			},
			{ path: "delay-codes", label: "Codes", icon: <TagOutlined /> },
		],
	},
	{
		name: "Adjustments",
		icon: <HandymanOutlined />,
		items: [
			{
				path: "adjustments-types",
				label: "Types",
				icon: <StyleOutlined />,
			},
			{
				path: "adjustments-reasons",
				label: "Reasons",
				icon: <CommentOutlined />,
			},
		],
	},
	{
		name: "Lab",
		icon: <ScienceOutlined />,
		items: [
			{
				path: "tensile-requirements",
				label: "Tensile Requirements",
				icon: <FitnessCenterOutlined />,
			},
			{
				path: "tests-by-section",
				label: "Tests by Section",
				icon: <FactCheckOutlined />,
			},
			{
				path: "chemistry-ranges",
				label: "Chemistry Ranges",
				icon: <Science />,
			},
			{
				path: "heat-age-thresholds",
				label: "Heat Age Thresholds",
				icon: <HourglassBottomOutlined />,
			},
		],
	},
	{
		name: "Utilities",
		icon: <BoltOutlined />,
		items: [
			{
				path: "water-allocations",
				label: "Water Allocations",
				icon: <WaterDropOutlined />,
			},
			{
				path: "gas-allocations",
				label: "Gas Allocations",
				icon: <LocalFireDepartmentOutlined />,
			},
			{
				path: "water-meter-thresholds",
				label: "Water Meter Thresholds",
				icon: <SpeedOutlined />,
			},
		],
	},
	{
		name: "Inspections",
		icon: <AssignmentTurnedInOutlined />,
		items: [
			{
				path: "inspection-templates",
				label: "Templates",
				icon: <ArticleOutlined />,
			},
			{
				path: "surface-defects",
				label: "Surface Defects",
				icon: <ReportProblemOutlined />,
			},
		],
	},
];

const routeComponents: Component[] = [
	{ path: "delay-categories", main: () => <CatalogCategories /> },
	{ path: "delay-codes", main: () => <CatalogDelayCodes /> },
	{ path: "locations", main: () => <CatalogLocation /> },
	{ path: "sections", main: Section },
	{ path: "sections/:sectionId", main: Section },
	{ path: "section-dimensions", main: () => <SectionDimensions /> },
	{ path: "section-families", main: () => <CatalogSectionFamily /> },
	{ path: "adjustments-types", main: () => <CatalogAdjustmentType /> },
	{ path: "adjustments-types/:typeId", main: () => <CatalogAdjustmentType /> },
	{ path: "adjustments-reasons", main: () => <CatalogAdjustmentReason /> },
	{
		path: "adjustments-reasons/:reasonId",
		main: () => <CatalogAdjustmentReason />,
	},
	{ path: "tensile-requirements", main: () => <TensileRequirements /> },
	{ path: "tests-by-section", main: () => <TestsBySection /> },
	{ path: "chemistry-ranges", main: () => <ChemistryRanges /> },
	{ path: "shift-report-config", main: () => <ShiftReportConfig /> },
	{ path: "heat-age-thresholds", main: () => <HeatAgeThresholds /> },
	{ path: "water-allocations", main: () => <WaterAllocations /> },
	{ path: "gas-allocations", main: () => <GasAllocations /> },
	{ path: "inspection-templates", main: () => <InspectionTemplates /> },
	{ path: "surface-defects", main: () => <SurfaceDefects /> },
	{ path: "water-meter-thresholds", main: () => <WaterMeterThresholds /> },
];

const components = routeComponents.map((route) => ({
	path: route.path,
	main: route.main,
}));

const useSidebarSections = (): ConfigSidebarSection[] => {
	const assetId = useAssetId();

	return sidebarSections
		.map((section) => ({
			...section,
			items: section.items.filter((item) =>
				isConfigurationAllowed(item.path, assetId),
			),
		}))
		.filter((section) => section.items.length > 0);
};

const customConfigurations: any = [components, null, useSidebarSections];

export { customConfigurations };
